import { el, clear } from '../dom';
import { rankMatch, scoreOf, improvementHint, type HeroResult, type RankedResult } from './scoring';
import { gicon } from '../ui/icons';

/**
 * Arena result screens (spec §11 "Result screens") — three screens in sequence, each
 * with a Next button: Survival → Kills → Final leaderboard. The final screen shows the
 * Score formula broken out line by line (the rules must be transparent) and a
 * "what to improve" coaching line (explain, don't just display).
 */

const FONT = 'font-family:"Terraboma","CuteFantasy",ui-sans-serif';
const MOLTEN = 'text-shadow: 0 2px 0 #7a1f0a';
// heroId → display nickname (set per match); falls back to the id.
let NAMES: Map<string, string> = new Map();
const nameOf = (id: string) => NAMES.get(id) ?? id;

/**
 * Mounts the three result screens and returns a disposer. The caller MUST own that
 * disposer: the overlay is a full-screen z-[60] layer, so a match torn down while it
 * is up (tab switch, view change) would otherwise leave it covering the whole app.
 */
export function showResultScreens(
  host: HTMLElement, results: HeroResult[], playerId: string, onClose: () => void, names?: Map<string, string>,
  onKeepWatching?: () => void,
): () => void {
  NAMES = names ?? new Map();
  const ranked = rankMatch(results);
  const me = ranked.find((r) => r.heroId === playerId) ?? ranked[ranked.length - 1];
  const overlay = el('div', 'arena-scene fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-3');
  host.appendChild(overlay);

  let done = false;
  // `dismiss()` is idempotent and fires onClose exactly once, whichever route out the
  // player takes: Done, the × , or Escape. `dispose()` is the silent variant the match
  // teardown uses — the view is going away, so there is nobody left to hand control to.
  const dispose = () => {
    if (done) return; done = true;
    document.removeEventListener('keydown', onKey, true);
    overlay.remove();
  };
  const dismiss = () => { if (done) return; dispose(); onClose(); };
  // A player killed mid-match gets the result window at once (the rewards and the way
  // out), but the match is still running behind it — so when the caller offers it, one
  // route out puts the overlay away and hands the camera back instead of leaving the
  // arena. Everything else (Done, ×, Escape) still exits.
  const keepWatching = onKeepWatching && (() => { if (done) return; dispose(); onKeepWatching(); });
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();       // Escape here closes the results, not the view underneath
    dismiss();
  };
  document.addEventListener('keydown', onKey, true);

  const screens = [survivalScreen, killsScreen, finalScreen];
  let i = 0;
  const render = () => {
    clear(overlay);
    // Lava-obsidian signboard, matching the arena entry / offer panels.
    const outer = el('div', 'arena-panel arena-font relative w-[min(420px,94vw)] max-h-full flex flex-col');
    const card = el('div', 'arena-panel-inner flex flex-col min-h-0 overflow-y-auto');
    outer.append(
      el('button', 'absolute top-1 right-2 z-10 text-2xl leading-none text-[#e9d6bd] hover:text-white',
        { title: 'Close (Esc)', onClick: dismiss }, '×'),
      card);
    screens[i](card, ranked, me);
    const next = el('button', 'arena-btn w-full mt-3 px-4 py-1.5 text-[13px]',
      { onClick: () => { i++; if (i >= screens.length) dismiss(); else render(); } },
      i < screens.length - 1 ? 'Next →' : 'Done');
    card.append(next);
    if (keepWatching) {
      card.append(el('button', 'arena-btn-ghost w-full mt-1.5 px-4 py-1.5 text-[12px]',
        { title: 'Close this and watch the rest of the match', onClick: keepWatching }, 'Keep watching'));
    }
    overlay.appendChild(outer);
  };
  render();
  return dispose;
}

/**
 * The headline word.
 *
 * It used to read VICTORY for anyone still breathing at the 210s timer, so a match
 * that ended with twelve fighters alive handed out twelve victories — "Victory, 1
 * of 25" with survivors on screen. A victory is 1st place and nothing else; every
 * other survivor SURVIVED, and the dead were ELIMINATED.
 */
function outcomeTitle(me: RankedResult): string {
  if (me.survivalPlace === 1) return 'VICTORY';
  return me.survived ? 'SURVIVED' : 'ELIMINATED';
}

function survivalScreen(card: HTMLElement, ranked: RankedResult[], me: RankedResult) {
  const killer = ranked.find((r) => r.heroId === (me as HeroResult & { killedBy?: string }).killedBy);
  card.append(
    el('div', 'text-[18px] tracking-wide text-[#ffb648] text-center mb-2', { style: MOLTEN }, outcomeTitle(me)),
    stat('Placement', `${me.survivalPlace} of ${ranked.length}`, me.survivalPlace <= 5 ? 'text-[#ffcf8a]' : ''),
    stat('Time alive', fmtTime(me.timeAliveMs)),
    stat('Outcome', me.survived ? 'Survived to the end' : killer ? `Eliminated by ${nameOf(killer.heroId)}` : 'Eliminated'),
  );
}

function killsScreen(card: HTMLElement, ranked: RankedResult[], me: RankedResult) {
  card.append(el('div', 'text-[18px] tracking-wide text-[#ffb648] text-center mb-1', { style: MOLTEN }, 'FRAGS'));
  card.append(el('div', 'text-center text-[11px] text-[#e7b58a] mb-2', {},
    `${me.kills} kills · ${me.goldBanked} gold banked`));
  // Frag leaderboard, player row highlighted.
  const board = [...ranked].sort((a, b) => b.kills - a.kills || b.goldBanked - a.goldBanked).slice(0, 8);
  for (const r of board) {
    const mine = r.heroId === me.heroId;
    card.append(el('div', `flex justify-between px-2 py-1 rounded text-[11px] ${mine ? 'bg-[#ffb648]/20 text-[#ffcf8a]' : 'text-[#e7b58a]'}`, {},
      // Pixel star sprite, not the dingbat: the ★ was the one non-pack glyph on
      // the results board.
      el('span', 'truncate inline-flex items-center gap-1', {},
        ...(mine ? [gicon('star', 11)] : []), nameOf(r.heroId)),
      el('span', 'font-mono', {}, `${r.kills}k`)));
  }
}

function finalScreen(card: HTMLElement, _ranked: RankedResult[], me: RankedResult) {
  const b = scoreOf(me);
  card.append(el('div', 'text-[18px] tracking-wide text-[#ffb648] text-center mb-2', { style: MOLTEN }, 'FINAL STANDING'));
  card.append(el('div', 'text-center text-[34px] leading-none text-[#ffcf8a] mb-1', { style: MOLTEN }, `#${me.rank}`));
  card.append(el('div', 'text-center text-[11px] text-[#e7b58a] mb-2.5', {}, `Score ${b.total}`));

  // The formula, broken out line by line (spec: transparent).
  const line = (label: string, val: number, note: string) =>
    el('div', 'flex justify-between text-[11px] font-mono py-0.5', {},
      el('span', '', {}, label), el('span', '', {}, `${val >= 0 ? '+' : ''}${val}  `),
      el('span', 'text-text-dim flex-1 text-right', {}, note));
  const box = el('div', 'bg-surface-2 rounded-lg p-2.5 mb-2.5', {},
    line(`Survival (place ${me.survivalPlace})`, b.survival, `(26−${me.survivalPlace})×10`),
    line(`Frags (${me.kills})`, b.frags, `×25`),
    line(`Gold (${me.goldBanked})`, b.gold, `÷10`),
    line('Survival bonus', b.survivalBonus, me.survived ? 'lasted to the end' : 'died — no bonus'),
    el('div', 'border-t border-border mt-1 pt-1 flex justify-between text-[11px] font-mono', {},
      el('span', 'text-gold', {}, 'Total'), el('span', 'text-gold', {}, String(b.total))));
  card.append(box);

  // Why it shook out this way (spec's explicit examples).
  if (!me.survived && me.kills > 0) {
    card.append(el('div', 'text-[11px] text-text-dim mb-2', { style: FONT },
      `You led on frags but died ${me.survivalPlace <= 5 ? 'late' : `${me.survivalPlace}th`} — survival weighs more.`));
  }
  // Coaching line.
  card.append(el('div', 'text-[11px] text-energy p-2 rounded bg-energy/10', { style: FONT },
    `What to improve: ${improvementHint(me)}`));
}

function stat(label: string, value: string, cls = '') {
  return el('div', 'flex justify-between py-1 border-b border-border/40', {},
    el('span', 'text-text-dim text-[11px]', {}, label),
    el('span', `text-[11px] ${cls}`, {}, value));
}

const fmtTime = (ms: number) => {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};
